/**
 * Game State Store - Client-side state management
 *
 * Central store for server-authoritative game state.
 * Receives state from server, emits events for UI/game updates.
 * This is the single source of truth for game state on the client.
 */

import { EventEmitter } from '../engine/core/EventEmitter';
import {
  CeeloState,
  DicePhase,
  DiceThrowParams,
  RollState,
  PlayerRollState,
  FullGameState,
} from '@sdl/shared';

// ============================================================================
// STORE EVENTS
// ============================================================================

interface GameStateStoreEvents {
  'game:state_changed': CeeloState;
  'dice:phase_changed': { playerId: string; phase: DicePhase; throwParams?: DiceThrowParams };
  'dice:result': { playerId: string; results: number[] };
  'roll:authorized': { phase: string; diceCount: number };
  'state:synced': void;
  'reconnect:token': string;
}

// ============================================================================
// STORE STATE
// ============================================================================

interface StoreState {
  // Connection state
  connected: boolean;
  reconnectionToken: string | null;

  // Game state (server-authoritative)
  gameState: CeeloState;
  roomId: string | null;
  stateVersion: number;

  // Players
  localPlayerId: string | null;
  bankerId: string | null;
  currentPlayerId: string | null;

  // Game context
  pot: number;
  roundNumber: number;
  timeRemaining: number;

  // Roll state
  rollState: RollState | null;
  playerRollStates: Map<string, PlayerRollState>;

  // HI_LO
  hiLoContenders: string[];
  hiLoRolls: Map<string, number>;
}

// ============================================================================
// GAME STATE STORE
// ============================================================================

export class GameStateStore extends EventEmitter<GameStateStoreEvents> {
  private state: StoreState;

  constructor() {
    super();

    this.state = {
      connected: false,
      reconnectionToken: null,
      gameState: CeeloState.WAITING,
      roomId: null,
      stateVersion: 0,
      localPlayerId: null,
      bankerId: null,
      currentPlayerId: null,
      pot: 0,
      roundNumber: 0,
      timeRemaining: 0,
      rollState: null,
      playerRollStates: new Map(),
      hiLoContenders: [],
      hiLoRolls: new Map(),
    };

    // Try to restore reconnection token from storage
    this.loadReconnectionToken();
  }

  // ============================================================================
  // GETTERS (Read-only access to state)
  // ============================================================================

  get gameState(): CeeloState {
    return this.state.gameState;
  }

  get roomId(): string | null {
    return this.state.roomId;
  }

  get stateVersion(): number {
    return this.state.stateVersion;
  }

  get localPlayerId(): string | null {
    return this.state.localPlayerId;
  }

  get bankerId(): string | null {
    return this.state.bankerId;
  }

  get currentPlayerId(): string | null {
    return this.state.currentPlayerId;
  }

  get isMyTurn(): boolean {
    return this.state.localPlayerId !== null &&
           this.state.currentPlayerId === this.state.localPlayerId;
  }

  get canRoll(): boolean {
    const state = this.state.gameState;

    // Practice mode
    if (state === CeeloState.WAITING) {
      return true;
    }

    // HI_LO - check if we're a contender who hasn't rolled
    if (state === CeeloState.HI_LO) {
      return this.state.localPlayerId !== null &&
             this.state.hiLoContenders.includes(this.state.localPlayerId) &&
             !this.state.hiLoRolls.has(this.state.localPlayerId);
    }

    // Normal turn
    return this.isMyTurn;
  }

  get dicePhase(): DicePhase {
    if (!this.state.localPlayerId) return DicePhase.IDLE;
    const rollState = this.state.playerRollStates.get(this.state.localPlayerId);
    return rollState?.phase ?? DicePhase.IDLE;
  }

  get rollState(): RollState | null {
    return this.state.rollState;
  }

  get reconnectionToken(): string | null {
    return this.state.reconnectionToken;
  }

  // ============================================================================
  // SETTERS
  // ============================================================================

  setLocalPlayerId(playerId: string | null): void {
    this.state.localPlayerId = playerId;
  }

  setConnected(connected: boolean): void {
    this.state.connected = connected;
  }

  // ============================================================================
  // STATE APPLICATION (from server events)
  // ============================================================================

  /**
   * Apply full state from server (for reconnection or initial sync)
   */
  applyFullState(fullState: FullGameState): void {
    const previousState = this.state.gameState;

    this.state.gameState = fullState.gameState as CeeloState;
    this.state.roomId = fullState.roomId;
    this.state.stateVersion = fullState.version;
    this.state.bankerId = fullState.bankerId;
    this.state.currentPlayerId = fullState.currentPlayerId;
    this.state.pot = fullState.pot;
    this.state.roundNumber = fullState.roundNumber;
    this.state.timeRemaining = fullState.timeRemaining;

    // Roll state
    this.state.rollState = fullState.rollState;

    // Player roll states
    this.state.playerRollStates.clear();
    for (const [playerId, rollState] of Object.entries(fullState.playerRollStates)) {
      this.state.playerRollStates.set(playerId, rollState);
    }

    // HI_LO state
    this.state.hiLoContenders = fullState.hiLoContenders;
    this.state.hiLoRolls.clear();
    for (const [playerId, roll] of Object.entries(fullState.hiLoRolls)) {
      this.state.hiLoRolls.set(playerId, roll);
    }

    this.emit('state:synced', undefined);

    if (previousState !== this.state.gameState) {
      this.emit('game:state_changed', this.state.gameState);
    }
  }

  /**
   * Handle dice phase change event from server
   */
  handleDicePhase(data: { playerId: string; phase: DicePhase; throwParams?: DiceThrowParams }): void {
    // Update player roll state
    let rollState = this.state.playerRollStates.get(data.playerId);
    if (!rollState) {
      rollState = {
        phase: data.phase,
        throwParams: data.throwParams ?? null,
        result: null,
      };
      this.state.playerRollStates.set(data.playerId, rollState);
    } else {
      rollState.phase = data.phase;
      if (data.throwParams) {
        rollState.throwParams = data.throwParams;
      }
    }

    this.emit('dice:phase_changed', data);

    console.log('[GameStateStore] Dice phase changed', {
      playerId: data.playerId,
      phase: data.phase,
      hasThrowParams: !!data.throwParams,
    });
  }

  /**
   * Handle dice result event from server
   */
  handleDiceResult(data: { playerId: string; results: number[] }): void {
    const rollState = this.state.playerRollStates.get(data.playerId);
    if (rollState) {
      rollState.result = data.results;
      rollState.phase = DicePhase.STOPPED;
    }

    // For HI_LO, track the roll
    if (this.state.gameState === CeeloState.HI_LO && data.results.length === 1) {
      this.state.hiLoRolls.set(data.playerId, data.results[0]);
    }

    this.emit('dice:result', data);

    console.log('[GameStateStore] Dice result', {
      playerId: data.playerId,
      results: data.results,
    });
  }

  /**
   * Handle roll authorization from server
   */
  handleRollAuthorize(data: { phase: string; diceCount: number }): void {
    // Clear any existing local roll state for fresh roll
    if (this.state.localPlayerId) {
      this.state.playerRollStates.delete(this.state.localPlayerId);
    }

    this.emit('roll:authorized', data);

    console.log('[GameStateStore] Roll authorized', data);
  }

  /**
   * Handle game state change from regular state update
   */
  handleGameState(gameState: string): void {
    const newState = gameState as CeeloState;
    if (this.state.gameState !== newState) {
      const previousState = this.state.gameState;
      this.state.gameState = newState;

      // Clear roll states when transitioning between phases
      if (this.shouldClearRollStates(previousState, newState)) {
        this.state.playerRollStates.clear();
      }

      this.emit('game:state_changed', newState);

      console.log('[GameStateStore] Game state changed', {
        from: previousState,
        to: newState,
      });
    }
  }

  // ============================================================================
  // RECONNECTION
  // ============================================================================

  setReconnectionToken(token: string): void {
    this.state.reconnectionToken = token;
    this.saveReconnectionToken(token);
    this.emit('reconnect:token', token);
  }

  private saveReconnectionToken(token: string): void {
    try {
      localStorage.setItem('sdl_reconnect_token', token);
      if (this.state.roomId) {
        localStorage.setItem('sdl_room_id', this.state.roomId);
      }
    } catch (e) {
      console.warn('[GameStateStore] Failed to save reconnection token', e);
    }
  }

  private loadReconnectionToken(): void {
    try {
      const token = localStorage.getItem('sdl_reconnect_token');
      if (token) {
        this.state.reconnectionToken = token;
      }
    } catch (e) {
      console.warn('[GameStateStore] Failed to load reconnection token', e);
    }
  }

  clearReconnectionData(): void {
    this.state.reconnectionToken = null;
    try {
      localStorage.removeItem('sdl_reconnect_token');
      localStorage.removeItem('sdl_room_id');
    } catch (e) {
      console.warn('[GameStateStore] Failed to clear reconnection data', e);
    }
  }

  getSavedRoomId(): string | null {
    try {
      return localStorage.getItem('sdl_room_id');
    } catch {
      return null;
    }
  }

  // ============================================================================
  // HELPERS
  // ============================================================================

  private shouldClearRollStates(from: CeeloState, to: CeeloState): boolean {
    // Clear roll states when moving between major phases
    const rollPhases = [CeeloState.HI_LO, CeeloState.BANKER_ROLL, CeeloState.PLAYER_ROLL];
    const wasRolling = rollPhases.includes(from);
    const isRolling = rollPhases.includes(to);

    return wasRolling !== isRolling || (from === CeeloState.HI_LO && to !== CeeloState.HI_LO);
  }

  /**
   * Reset store to initial state
   */
  reset(): void {
    this.state = {
      connected: false,
      reconnectionToken: this.state.reconnectionToken, // Keep token for reconnection
      gameState: CeeloState.WAITING,
      roomId: null,
      stateVersion: 0,
      localPlayerId: this.state.localPlayerId, // Keep player ID
      bankerId: null,
      currentPlayerId: null,
      pot: 0,
      roundNumber: 0,
      timeRemaining: 0,
      rollState: null,
      playerRollStates: new Map(),
      hiLoContenders: [],
      hiLoRolls: new Map(),
    };
  }
}

// Singleton instance
export const gameStateStore = new GameStateStore();
